import React from 'react';
import { Link } from 'react-router-dom';
import {
  calculateOntarioLandTransferTax,
  ONTARIO_FTHB_REBATE_CAP,
  TORONTO_FTHB_REBATE_CAP,
} from '../../../utils/ontarioLandTransferTax';
import { calculateCanadianMinimumDownPayment } from '../../../utils/mortgageCalculations';
import { formatUsd } from '../../../utils/articleScenarioMetrics';

const TORONTO_PRICE = 900_000;
const OTTAWA_PRICE = 650_000;
const torontoLtt = calculateOntarioLandTransferTax(TORONTO_PRICE);
const ottawaLtt = calculateOntarioLandTransferTax(OTTAWA_PRICE);
const ottawaMinDown = calculateCanadianMinimumDownPayment(OTTAWA_PRICE);
const ottawaBaseLoan = OTTAWA_PRICE - ottawaMinDown;
const ottawaCmhcPremium = Math.round(ottawaBaseLoan * 0.031);
const ottawaCmhcPst = Math.round(ottawaCmhcPremium * 0.08);

export const OntarioCalculatorContext: React.FC = () => (
  <div className="prose prose-stone max-w-none text-ink-muted space-y-4">
    <h3 className="font-serif text-xl text-ink">How Ontario closing costs differ from your payment</h3>
    <p>
      The Toronto tab models a {formatUsd(TORONTO_PRICE)} purchase with 20% down at 5.20% fixed. Land
      transfer tax is due in cash at closing, not in the monthly mortgage payment. Inside Toronto city limits,
      buyers pay provincial land transfer tax and municipal land transfer tax on the same graduated tiers.
      On {formatUsd(TORONTO_PRICE)}, gross provincial tax is {formatUsd(torontoLtt)} before rebates. Toronto
      charges the same amount again as municipal land transfer tax, for {formatUsd(torontoLtt * 2)} combined
      on a repeat buyer purchase.
    </p>
    <p>
      First-time buyers who qualify can claim up to {formatUsd(ONTARIO_FTHB_REBATE_CAP)} off the provincial
      portion and up to {formatUsd(TORONTO_FTHB_REBATE_CAP)} off the municipal portion. The Ottawa tab uses
      a {formatUsd(OTTAWA_PRICE)} price with 10% down and CMHC insurance. Outside Toronto, only provincial
      land transfer tax applies: {formatUsd(ottawaLtt)} gross on that price. Stepped minimum down payment rules
      mean a {formatUsd(OTTAWA_PRICE)} home requires {formatUsd(ottawaMinDown)} down (5% on the first $500,000
      plus 10% on the remainder), not a flat 10%.
    </p>
    <p>
      When CMHC insurance applies, Ontario charges 8% provincial sales tax on the premium at closing. On the
      Ottawa scenario, a {formatUsd(ottawaCmhcPremium)} premium triggers about {formatUsd(ottawaCmhcPst)} in
      PST due in cash. The premium itself capitalizes into the loan balance. Compare the Toronto and Ottawa
      tabs side by side to see how a higher GTA price with conventional financing stacks up against an insured
      Ottawa purchase with higher property tax mill rates.
    </p>
    <p className="text-xs text-ink-muted border-l-4 border-stone-200 pl-3">
      Sources: Ontario Ministry of Finance, &quot;Calculating Land Transfer Tax,&quot; accessed June 2026. URL:{' '}
      <a
        href="https://www.ontario.ca/document/land-transfer-tax/calculating-land-transfer-tax"
        className="text-gold-700 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.ontario.ca/document/land-transfer-tax/calculating-land-transfer-tax
      </a>
      . Canada Mortgage and Housing Corporation, &quot;CMHC mortgage loan insurance costs,&quot; accessed June 2026. URL:{' '}
      <a
        href="https://www.cmhc-schl.gc.ca/consumers/home-buying/mortgage-loan-insurance-for-consumers/cmhc-mortgage-loan-insurance-cost"
        className="text-gold-700 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.cmhc-schl.gc.ca/consumers/home-buying/mortgage-loan-insurance-for-consumers/cmhc-mortgage-loan-insurance-cost
      </a>
      . See also our{' '}
      <Link to="/learn/guides/mortgage-calculator-ontario-guide" className="text-gold-700 hover:underline">
        Ontario mortgage guide
      </Link>
      .
    </p>
  </div>
);
